import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Text } from '@uhg-abyss/web/ui/Text';
import mapboxgl from 'mapbox-gl';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { CardSection } from '../../../../../common/CardSection/CardSection';
import {
  Constants,
  PopulationType,
  SHOULD_GET_HG_DATA,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../../common/ContentWrapper';
import { ImageComponent } from '../../../../../common/ImageComponent';
import { Breadcrumb } from '../../../../../common/PSXHeader/Breadcrumb';
import {
  convertObjectToUrlParams,
  getFeatureFlag,
} from '../../../../../common/Utils';
import { determineIsSuperUser } from '../../../../../common/Widget/SmartChoiceWidget';
import {
  createPostProviderPayload,
  getGuestPlanSelectionData,
  postProviderInfo,
  triggerResponseDataUpdated,
} from '../../../../../frontends/GuestExperience/Guest.utils';
import { useAdobeBrowserBackButtonTrack } from '../../../../../hooks/adobeHook/useAdobeBrowserBackButtonTrack';
import { useAdobePageTrackEvent } from '../../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useDeleteGuestAcquisition } from '../../../../../hooks/useDeleteGuestAcquisition';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import {
  useClearPrimaryCareCache,
  usePrimaryCare,
  useUpdatePrimaryCare,
} from '../../../../../hooks/usePrimaryCare';
import { useProviderDetails } from '../../../../../hooks/useProviderDetails';
import { useSteps } from '../../../../../hooks/useSteps';
import { BreadcrumbSessionStorage } from '../../../../../models/BreadcrumbSessionStorage';
import {
  NetworkAffiliationDetails,
  ProviderDetails,
  ProviderLocation,
} from '../../../../../models/ProviderDetails';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../../../../store/useGuestAcquisitionStore';
import {
  GuestAcquisitionState,
  ProviderDetailsMaestroResponse,
} from '../../../../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { useGuestStore } from '../../../../../store/useGuestStore';
import { GuestState } from '../../../../../store/useGuestStore/guestStore';
import { usePCPStore } from '../../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../../store/useTypeaheadStore/typeaheadStore';
import { searchResultBreadcrumbTitle } from '../../../../../utils/common';
import { clearRoute } from '../../../../../utils/map.utils';
import { getPrimaryPhone } from '../../../../../utils/phone.utils';
import {
  getPCPGetParams,
  getPCPUpdateParams,
} from '../../../../../utils/primaryCare.utils';
import {
  getProviderLocationWithProviderGroup,
  getSelectedLocationIndex,
} from '../../../../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../../../utils/providerNames.utils';
import { removeLeadingZeroes } from '../../../../../utils/string.utils';
import {
  getCurrentMember,
  getLoggedInMember,
  getMemberByDependentSeqNbr,
  getNetworkIdsForPES,
} from '../../../../../utils/user.utils';
import { filterNetworkAffiliations } from '../../SearchResults/searchUtils';
import LocationSaved from '../ChoosePCPSuccess/LocationSaved';
import { ChooseNetworkAffiliation } from './ChooseNetworkAffiliation';
import {
  ButtonRowBox,
  ChooseLocationHeading,
  ChoosePCPLocationAvatar,
  ContentWrapperFullWidth,
  LocationWrapperFullWidth,
  PCPCardWrapper,
  PCPHeaderContainer,
  SelectedPCPCard,
  StyledProviderName,
} from './ChoosePCPLocation.styled';
import { ChoosePCPLocationButtonRow } from './ChoosePCPLocationButtonRow';
import { ChoosePCPLocationHeading } from './ChoosePCPLocationHeading';
import { ConfirmPCPLocation } from './ConfirmPCPLocation';
import { LocationsListView } from './LocationListView';
import { LocationsMapViewWrap } from './LocationsMapViewWrap';
import { UpdateErrorModal } from './UpdateErrorModal';
import { UpdatePCPErrorModal } from './UpdatePCPErrorModal';
import { UpdatePCPErrorView } from './UpdatePCPErrorView';

const ERROR_BUTTON_VARIANT_CODES = new Set([
  '50085',
  '50126',
  '50128',
  '50135',
]);
const isErrorButtonVariant = (code: string): boolean =>
  ERROR_BUTTON_VARIANT_CODES.has(code);

const parseBreadcrumbs = (
  urls: BreadcrumbSessionStorage,
  t: (key: string) => string
) => {
  const breadcrumbs = [
    {
      title: t('Select location'),
      href: '',
    },
  ];
  if (urls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key]) {
    const breadcrumbToken =
      tokenizer.parse(
        urls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key].replace(
          '/results/mapview/',
          ''
        )
      ) || {};
    breadcrumbs.unshift({
      title: t(searchResultBreadcrumbTitle(breadcrumbToken.sectionType)),
      href: `${urls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key]}`,
    });
  }
  if (urls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key]) {
    breadcrumbs.unshift({
      title: t('BC Results'),
      href: `${urls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key]}`,
    });
  }
  return breadcrumbs;
};

const computeMaestroSaveData = (
  isUpdateProvider: boolean,
  provider: ProviderDetails,
  responseData: ProviderDetailsMaestroResponse[],
  savedLocationOption: ProviderLocation[],
  networkAffiliationData: NetworkAffiliationDetails | null
) => {
  const selectedLocation = [...savedLocationOption];
  if (isUpdateProvider) {
    const index = responseData.findIndex(
      (item) =>
        removeLeadingZeroes(item.providerId) ===
        removeLeadingZeroes(provider?.providerId)
    );
    if (index !== -1) {
      const findProvider = responseData[index];
      const selectedLocationIds = new Set(
        selectedLocation.map((location) => location.locationId)
      );
      const removedLocation = findProvider.locations.filter(
        (location) => !selectedLocationIds.has(location.locationId)
      );
      const updatedProvider = {
        ...findProvider,
        locations: selectedLocation,
        networkAffiliationData,
      };
      const newProvider = [...responseData];
      newProvider[index] = updatedProvider;
      return {
        actionLocation: {
          provider: updatedProvider,
          addedLocation: selectedLocation,
          removedLocation,
        },
        updatedResponseData: newProvider,
      };
    }
    return null;
  }
  const newProvider = {
    ...provider,
    locations: selectedLocation,
    networkAffiliationData,
  };
  const newAddProvider = [...responseData, newProvider];
  return {
    actionLocation: {
      provider: newProvider,
      addedLocation: selectedLocation,
      removedLocation: [] as ProviderLocation[],
    },
    updatedResponseData: newAddProvider,
  };
};

const hasValidNetworkAffiliations = (
  location: ProviderLocation,
  enableNetworkAffiliationFlow: boolean
): boolean =>
  enableNetworkAffiliationFlow &&
  filterNetworkAffiliations(location.networks).length > 0;

const isAcceptingPcpPatients = (location: ProviderLocation): boolean =>
  !!location.acceptingNewPatients && !!location.hasPCPServiceAvailability;

const hasNetworkAffiliationSelected = (
  enableNetworkAffiliationFlow: boolean,
  selectedNetworkAffiliation: NetworkAffiliationDetails | null
): boolean => enableNetworkAffiliationFlow && !!selectedNetworkAffiliation;

const applyNetworkAffiliationContinue = (
  affiliation: NetworkAffiliationDetails | null,
  isNavigatingMaestro: boolean,
  setSelectedNetworkAffiliation: (v: NetworkAffiliationDetails | null) => void,
  setCurrentStep: (
    step: 'location' | 'network-affiliation' | 'confirm'
  ) => void,
  onMaestroSave: (v: NetworkAffiliationDetails | null) => void
) => {
  setSelectedNetworkAffiliation(affiliation);
  setCurrentStep('confirm');
  if (isNavigatingMaestro) {
    onMaestroSave(affiliation);
  }
};

const getContentWrapperComponent = (flag: boolean) =>
  flag ? ContentWrapperFullWidth : ContentWrapper;

const getSelectedPCPCardHeight = (removeTruncatedDataCard: boolean): string =>
  removeTruncatedDataCard ? 'auto' : '66px';

export const ChoosePCPLocation = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const map = React.useRef<mapboxgl.Map | null>(null);
  const { t } = useTranslation();
  const featureFlags = useLagoon('feature-flags')();
  const { navigate } = useRouter();
  const [enablePCPParamsFromURL] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PCP_PARAMS_FROM_URL,
  ]);

  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const {
    isNavigatingMaestro,
    responseData,
    setResponseData,
    isUpdateProvider,
    isNavigatingFromJarvis,
    requestData,
  } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
      setResponseData: state.setResponseData,
      responseData: state.responseData,
      isUpdateProvider: state.isUpdateProvider,
      isNavigatingFromJarvis: state.isNavigatingFromJarvis,
      requestData: state.requestData,
    }))
  );
  const currentMember = getCurrentMember();
  const { isIFPEarlyPay } = currentMember;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType } = chipStore;
  const { sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const choosePCPIdFromParams = urlParams.get('choosePCPId');
  const selectedProviderTypeFromParams = urlParams.get('selectedProviderType');
  const dependentSeqNbrFromParams = urlParams.get('dependentSeqNbr');
  const {
    setPCPSearchState,
    choosePCPId: choosePCPIdFromStore,
    dependentSeqNbr: dependentSeqNbrFromStore,
    selectedProviderType: selectedProviderTypeFromStore,
  } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      choosePCPId: state.pcpSearchState.choosePCPId,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
      selectedProviderType: state.pcpSearchState.selectedProviderType,
    }))
  );

  const choosePCPId =
    choosePCPIdFromStore || (enablePCPParamsFromURL && choosePCPIdFromParams);

  const dependentSeqNbr: string | undefined =
    dependentSeqNbrFromStore ||
    (enablePCPParamsFromURL && dependentSeqNbrFromParams) ||
    undefined;

  const selectedProviderType =
    selectedProviderTypeFromStore ||
    (enablePCPParamsFromURL && selectedProviderTypeFromParams);

  const [
    viewMapEnhancementsFlag,
    suppressPcpSelectionCommit,
    enableNetworkAffiliationFlow,
    enableAllStaffModelEni,
    enableAllStaffModelMnr,
    removeTruncatedDataCard,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.SUPPRESS_PCP_SELECTION_COMMIT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_MNR,
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);
  const ContentWrapperComponent = getContentWrapperComponent(
    viewMapEnhancementsFlag
  );

  const LocationWrapperComponent = viewMapEnhancementsFlag
    ? LocationWrapperFullWidth
    : ContentWrapper;

  const loggedInMember = getLoggedInMember();
  const memberByDependentSeqNbr = getMemberByDependentSeqNbr(dependentSeqNbr);
  const { selectedPlan } = getGuestPlanSelectionData();

  const { data: primaryCareData } = usePrimaryCare();
  const { primaryCare } = primaryCareData;
  const curPCP = primaryCare?.find((pcp) => pcp.depSeqNbr === dependentSeqNbr);

  const [, updatePCP] = useUpdatePrimaryCare({});
  const [, clearPCPCache] = useClearPrimaryCareCache({});
  const [, deleteGuestAcquisition] = useDeleteGuestAcquisition({});
  const imgRef = useRef<HTMLImageElement>(null);

  const [isMobileMapView, setIsMobileMapView] = useState<boolean>(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);
  const [isUpdatingPCP, setIsUpdatingPCP] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showErrorView, setShowErrorView] = useState<boolean>(false);
  const [showButtonVariant, setShowButtonVariant] = useState<boolean>(false);
  const [mobileRouteView, setMobileRouteView] = useState<boolean>(false);
  const [routeEndCoords, setRouteEndCoords] = useState<
    [number | null, number | null]
  >([null, null]);
  const [errorCode, setErrorCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [mockPopulationType, setMockPopulationType] = useState<PopulationType>(
    PopulationType.UNKNOWN
  );
  const [selectedNetworkAffiliation, setSelectedNetworkAffiliation] =
    useState<NetworkAffiliationDetails | null>(null);
  const [
    shouldSimulateUpdatePcpErrorModal,
    setShouldSimulateUpdatePcpErrorModal,
  ] = useState<boolean>(false);

  const [selectedId, setSelectedId] = useSessionStorage<string | null>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );
  const [currentSelectedId, setCurrentSelectedId] = useSessionStorage<
    string | null
  >(Constants.STORAGE_KEYS.SESSION.PROVIDER_MAP_PIN_SELECTED_ID, null);

  const { USE_PRE_PROD } = Constants.STORAGE_KEYS.SESSION;

  const [enablePreProd] = useState(storage.session.get(USE_PRE_PROD, false));
  const [isSavedLocation, setIsSavedLocation] = useState<boolean>(false);
  const [actionLocation, setActionLocation] = useState({
    provider: {} as ProviderDetailsMaestroResponse,
    addedLocation: [] as ProviderLocation[],
    removedLocation: [] as ProviderLocation[],
  });
  const [savedLocationOption, setSavedLocationOption] = useState<
    ProviderLocation[]
  >([]);
  const [selectedProviderAffiliation, setNetworkAffiliationForProvider] =
    useState<NetworkAffiliationDetails | null>(null);
  useEffect(() => {
    if (isNavigatingMaestro && isUpdateProvider) {
      const maestroEntry = responseData.find(
        (item) =>
          removeLeadingZeroes(item.providerId) ===
          removeLeadingZeroes(choosePCPId || '')
      );
      if (maestroEntry) {
        setNetworkAffiliationForProvider(
          maestroEntry.networkAffiliationData ?? null
        );
        setSavedLocationOption(maestroEntry.locations);
      }
    }
  }, []);
  useEffect(() => {
    storage.session.set(USE_PRE_PROD, enablePreProd);
  }, [enablePreProd]);

  const choosePcpLocation: string = 'body:choose pcp location';

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'choose location',
    sitesectionLevel1: 'pcp',
  });

  const { adobeBrowserBackButtonTrack } = useAdobeBrowserBackButtonTrack();

  const [memberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );
  const [selectedLocationId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  const { data, isLoading } = useProviderDetails({
    providerType: selectedProviderType,
    providerId: choosePCPId,
    shouldGetHGData: SHOULD_GET_HG_DATA,
  });

  const [breadcrumbSessionStorage, setBreadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  const { currentStep, setCurrentStep, getStepUrl } = useSteps({
    steps: ['location', 'network-affiliation', 'confirm'] as const,
    baseUrl:
      breadcrumbSessionStorage[ConstantsRoutes.CHOOSE_PCP_LOCATION.key] || '',
    onStepChange: (step) => {
      if (step === 'location') {
        setSelectedNetworkAffiliation(null);
      }
    },
  });

  useEffect(() => {
    adobeBrowserBackButtonTrack();
  }, []);

  useEffect(() => {
    if (!mobileScreen) setIsMobileMapView(mobileScreen);
  }, [mobileScreen]);

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  useEffect(() => {
    const pathname =
      isNavigatingMaestro || isNavigatingFromJarvis
        ? window.location.pathname.replace('/guest-plan-selection/browse', '')
        : window.location.pathname.replace('/findcare', '');

    breadcrumbSessionStorage[ConstantsRoutes.CHOOSE_PCP_LOCATION.key] =
      pathname + window.location.search;
    setBreadcrumbSessionStorage(breadcrumbSessionStorage);
  }, []);

  const breadcrumbs = parseBreadcrumbs(breadcrumbSessionStorage, t);

  const allConfigs = useLagoon('config');
  const isSuperUserValue = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  const isSuperUser = determineIsSuperUser(allConfigs, isSuperUserValue);

  const providerDetails =
    data?.facilityDetails?.providerDetails ||
    data?.providerDetails?.providerDetails;

  if (!providerDetails) return null;

  const selectedPCPCardHeight = getSelectedPCPCardHeight(
    removeTruncatedDataCard
  );

  if (isLoading) {
    return (
      <CardSection>
        <LoadingSpinner
          ariaLoadingLabel="loading results"
          isLoading={isLoading}
          size="xs"
        />
      </CardSection>
    );
  }

  const provider = providerDetails as ProviderDetails;

  const {
    providerName,
    professionalDesignation,
    specialties,
    primaryDegrees,
    providerType,
    imageLocation,
  } = provider;

  const nameAttrs = getNameAttrs(provider);

  const formattedProviderName = parseProviderName(
    providerName,
    providerType,
    professionalDesignation,
    nameAttrs
  );

  const fullProviderName = primaryDegrees
    ? `${formattedProviderName}, ${primaryDegrees?.[0]}`
    : formattedProviderName;

  let providerLocations = isNavigatingMaestro
    ? provider?.providerLocations
    : getProviderLocationWithProviderGroup(provider?.providerLocations)?.filter(
        (location) =>
          hasValidNetworkAffiliations(location, enableNetworkAffiliationFlow) ||
          isAcceptingPcpPatients(location)
      );

  if (providerLocations.length === 0) {
    navigate(-1);
    return null;
  }

  const hasMultipleLocations = providerLocations?.length > 1;

  const isLocationSelected = !hasMultipleLocations || selectedLocationId;
  const isLocationSelectedMaestro = savedLocationOption.length > 0;
  const useStickyButtonRow = hasMultipleLocations && mobileScreen;

  const handleBackButtonClick = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: choosePcpLocation,
      type: 'internal',
    });

    if (enableNetworkAffiliationFlow && selectedNetworkAffiliation) {
      setCurrentStep('network-affiliation');
    } else if (isMobileMapView) {
      setIsMobileMapView(false);
      setMobileRouteView(false);
      clearRoute(map.current, setRouteEndCoords);
    } else {
      navigate(-1);
    }
  };

  const handleShowErrorModalSimulationSubmit = (
    errorCode,
    errorMessage?,
    populationType?
  ) => {
    setErrorCode(errorCode);
    setErrorMessage(errorMessage);
    setMockPopulationType(populationType);
    setShouldSimulateUpdatePcpErrorModal(true);
  };

  const handleShowErrorModalSimulationReset = () => {
    setShouldSimulateUpdatePcpErrorModal(false);
  };

  const handleCancel = () => {
    adobeLinkTrackEvent({
      name: 'cancel',
      location: choosePcpLocation,
      type: 'internal',
    });

    const selectedLocationIndex = getSelectedLocationIndex(
      providerLocations,
      currentSelectedId
    );
    setSelectedId(providerLocations[selectedLocationIndex].locationId);
    navigate(-1);
  };
  const handleCloseModal = () => setShowErrorModal(false);
  const selectedLocation = providerLocations[selectedAddressIndex];
  const phone = selectedLocation?.phones?.phone;
  const appointment = selectedLocation?.phones?.appointment;

  const primaryPhoneNumber = getPrimaryPhone(phone, appointment);
  const population = loggedInMember?.population;
  const errorModalPopulationType = shouldSimulateUpdatePcpErrorModal
    ? mockPopulationType
    : population;

  const showConfirmPCPLocationFF: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.CONFIRM_PCP_LOCATION
  );

  const showConfirmModal =
    currentStep !== 'confirm' && showConfirmPCPLocationFF;

  const maestroSelected = responseData.find(
    (item) =>
      removeLeadingZeroes(item.providerId) ===
      removeLeadingZeroes(provider?.providerId)
  );
  const maestroSelectedLocation = maestroSelected?.locations;
  if (isNavigatingMaestro && isUpdateProvider) {
    const selectedLocationIds = new Set(
      maestroSelectedLocation?.map((location) => location.locationId)
    );
    providerLocations = [
      ...providerLocations.filter((location) =>
        selectedLocationIds.has(location.locationId)
      ),
      ...providerLocations.filter(
        (location) => !selectedLocationIds.has(location.locationId)
      ),
    ];
  }
  const handleFinishAndReturn = async () => {
    const reciprocityId = getNetworkIdsForPES(
      currentMember,
      coverageType,
      featureFlags
    );
    const payload = createPostProviderPayload(
      requestData,
      responseData,
      selectedPlan,
      coverageType,
      reciprocityId,
      isNavigatingFromJarvis
    );
    if (isNavigatingFromJarvis) {
      document.getElementById('jarvisPostbackData').value =
        JSON.stringify(payload);
      document.getElementById('jarvisForm')?.submit();
      const transactionId = requestData?.transactionId;
      if (transactionId) {
        await deleteGuestAcquisition({
          variables: { transactionId },
        });
      }
      return;
    } else {
      const { success, html } = await postProviderInfo(
        requestData?.targetUrl,
        payload
      );
      if (!success) return;

      setResponseData([]);
      const transactionId = requestData?.transactionId;
      if (transactionId) {
        await deleteGuestAcquisition({
          variables: { transactionId },
        });
      }
      if (html) {
        document.open();
        document.write(html);
        document.close();
      }
    }
  };
  const saveMaestroProviderData = (
    networkAffiliationData: NetworkAffiliationDetails | null
  ) => {
    const result = computeMaestroSaveData(
      isUpdateProvider,
      provider,
      responseData,
      savedLocationOption,
      networkAffiliationData
    );
    if (result) {
      setActionLocation(result.actionLocation);
      setResponseData(result.updatedResponseData);
      triggerResponseDataUpdated(result.updatedResponseData);
    }
    setIsSavedLocation(true);
    return;
  };

  const handleSaveLocation = async () => {
    // for guest provider selection
    if (currentStep === 'location') {
      const networkAffiliations = filterNetworkAffiliations(
        providerLocations[selectedAddressIndex]?.networks,
        isNavigatingMaestro
      );
      if (enableNetworkAffiliationFlow && networkAffiliations.length > 0) {
        setCurrentStep('network-affiliation');
        return;
      }
    }
    if (isNavigatingMaestro) {
      saveMaestroProviderData(null);
    }

    if (isNavigatingFromJarvis) {
      const selectedLocation = providerLocations[selectedAddressIndex];
      const pcpSelected = {
        ...provider,
        locations: [selectedLocation],
      };
      setResponseData([pcpSelected]);
      setCurrentStep('confirm');
      return;
    }

    if (shouldSimulateUpdatePcpErrorModal) {
      setShowErrorModal(true);
      return;
    }
    if (showConfirmModal) {
      adobeLinkTrackEvent({
        name: 'save location confirm',
        location: choosePcpLocation,
        type: 'internal',
      });

      setCurrentStep('confirm');
      return;
    }

    adobeLinkTrackEvent({
      name: 'save location',
      location: choosePcpLocation,
      type: 'internal',
    });

    const selectedLocationString = selectedLocation
      ? JSON.stringify(selectedLocation)
      : null;
    if (!isSuperUser && !suppressPcpSelectionCommit) {
      setIsUpdatingPCP(true);
      const selectedLocation = providerLocations[selectedAddressIndex];

      const updateVariables = getPCPUpdateParams(
        dependentSeqNbr,
        loggedInMember,
        memberByDependentSeqNbr,
        memberId.toString(),
        selectedLocation.pcpId,
        providerType,
        providerName,
        guestData,
        isIFPEarlyPay,
        selectedNetworkAffiliation,
        currentMember?.isUspNiceMember ?? false
      );

      const result = await updatePCP({
        variables: updateVariables,
      });
      setIsUpdatingPCP(false);
      if (!result?.data?.updatePCP?.success) {
        setCurrentStep('location');
        if (isIFPEarlyPay) {
          setShowErrorView(true);
        } else {
          setShowErrorModal(true);
          setErrorCode(result.data.updatePCP.code);
          setErrorMessage(result.data.updatePCP.message);
          setShowButtonVariant(
            isErrorButtonVariant(result.data.updatePCP.code)
          );
        }
        return;
      }

      if (!isIFPEarlyPay) {
        clearPCPCache({
          variables: getPCPGetParams(
            enablePreProd,
            loggedInMember,
            memberId.toString()
          ),
        });
      }

      const selectedLocationIndex = getSelectedLocationIndex(
        providerLocations,
        selectedId
      );
      const locationId = providerLocations[selectedLocationIndex].locationId;
      setSelectedId(locationId);
      setCurrentSelectedId(locationId);
      setPCPSearchState({
        formattedProviderName,
        providerType,
        selectedLocation: selectedLocationString,
        primarySpeciality: specialties[0],
        pcpEffectiveDate: result?.data?.updatePCP?.pcpEffectiveDate,
        selectedNetworkAffiliationId:
          selectedNetworkAffiliation?.networkAffiliationId,
      });
      const successParams = enablePCPParamsFromURL
        ? {
            providerType,
            choosePCPId,
            currentSelectedId: locationId,
            dependentSeqNbr,
            selectedNetworkAffiliationId:
              selectedNetworkAffiliation?.networkAffiliationId,
          }
        : {
            formattedProviderName,
            providerType,
            selectedLocation: selectedLocationString,
            primarySpeciality: specialties[0],
            imageLocation,
            pcpEffectiveDate: result?.data?.updatePCP?.pcpEffectiveDate,
            selectedNetworkAffiliationId:
              selectedNetworkAffiliation?.networkAffiliationId,
          };
      const successUrl = convertObjectToUrlParams(chipStore, successParams);
      navigate(`${ConstantsRoutes.CHOOSE_PCP_SUCCESS.path}${successUrl}`);
    } else {
      // Bypass the OBAPI call so that super user does not make PCP updates for the member's profile/account.
      setPCPSearchState({
        formattedProviderName,
        providerType,
        selectedLocation: selectedLocationString,
        primarySpeciality: specialties[0],
        imageLocation,
        selectedNetworkAffiliationId:
          selectedNetworkAffiliation?.networkAffiliationId,
      });
      const successParams = enablePCPParamsFromURL
        ? {
            providerType,
            choosePCPId,
            currentSelectedId,
            dependentSeqNbr,
            selectedNetworkAffiliationId:
              selectedNetworkAffiliation?.networkAffiliationId,
          }
        : {
            sectionType,
            choosePCPId,
            dependentSeqNbr,
            selectedProviderType,
            selectedNetworkAffiliationId:
              selectedNetworkAffiliation?.networkAffiliationId,
          };
      const successUrl = convertObjectToUrlParams(chipStore, successParams);
      navigate(`${ConstantsRoutes.CHOOSE_PCP_SUCCESS.path}${successUrl}`);
    }
  };

  const handleDelete = () => {
    const deleteProvider = responseData.filter(
      (item) =>
        removeLeadingZeroes(item.providerId) !==
        removeLeadingZeroes(provider?.providerId)
    );
    if (maestroSelected) {
      setActionLocation({
        provider: { ...maestroSelected },
        addedLocation: [],
        removedLocation: maestroSelected.locations,
      });
    }
    setResponseData(deleteProvider);
    triggerResponseDataUpdated(deleteProvider);
    setIsSavedLocation(true);
  };

  const handleCancelConfirm = () => {
    adobePageTrackEvent();
    if (enableNetworkAffiliationFlow && selectedNetworkAffiliation) {
      setCurrentStep('network-affiliation');
    } else {
      setSelectedNetworkAffiliation(null);
      const selectedLocationIndex = getSelectedLocationIndex(
        providerLocations,
        currentSelectedId
      );
      setSelectedId(providerLocations[selectedLocationIndex].locationId);
      setSelectedAddressIndex(selectedLocationIndex);
      setCurrentStep('location');
    }
  };

  const handleNetworkAffiliationContinue = (
    affiliation: NetworkAffiliationDetails | null
  ) =>
    applyNetworkAffiliationContinue(
      affiliation,
      isNavigatingMaestro,
      setSelectedNetworkAffiliation,
      setCurrentStep,
      saveMaestroProviderData
    );

  const handleCancelNetworkAffiliation = () => {
    setSelectedNetworkAffiliation(null);
    setCurrentStep('location');
  };

  const saveAndCancelButtons = (
    <ChoosePCPLocationButtonRow
      handleCancel={handleCancel}
      handleDelete={handleDelete}
      handleSaveLocation={handleSaveLocation}
      hideDeleteBtn={!!selectedProviderAffiliation?.networkAffiliationId}
      isLocationSelected={
        isNavigatingMaestro ? isLocationSelectedMaestro : isLocationSelected
      }
      isNavigatingMaestro={isNavigatingMaestro}
      isUpdateProvider={isUpdateProvider}
      isUpdatingPCP={isUpdatingPCP}
    />
  );
  const imgSrc = imageLocation;
  if (isSavedLocation && isNavigatingMaestro) {
    return <LocationSaved actionLocation={actionLocation} />;
  }
  if (currentStep === 'network-affiliation') {
    const selectedLoc = providerLocations[selectedAddressIndex];
    const networkAffiliations = filterNetworkAffiliations(
      selectedLoc?.networks,
      isNavigatingMaestro
    );
    const networkAffiliationBreadcrumbs = [
      ...breadcrumbs.slice(0, -1),
      {
        title: t('Select location'),
        href: getStepUrl('location'),
      },
      {
        title: t('NETWORK_AFFILIATION.CHOOSE_NETWORK_AFFILIATION'),
        href: '',
      },
    ];
    return (
      <ChooseNetworkAffiliation
        breadcrumbs={networkAffiliationBreadcrumbs}
        currentMember={currentMember}
        handleBackButtonClick={handleCancelNetworkAffiliation}
        handleContinue={handleNetworkAffiliationContinue}
        handleDelete={handleDelete}
        imageLocation={imageLocation}
        isDeleteButtonVisible={
          !!(
            isNavigatingMaestro &&
            selectedProviderAffiliation?.networkAffiliationId
          )
        }
        networkAffiliations={networkAffiliations}
        providerName={fullProviderName}
        providerType={providerType}
        selectedProviderAffiliation={
          selectedNetworkAffiliation || selectedProviderAffiliation
        }
        specialty={specialties?.[0]}
      />
    );
  }

  const confirmBreadcrumbs = hasNetworkAffiliationSelected(
    enableNetworkAffiliationFlow,
    selectedNetworkAffiliation
  )
    ? [
        ...breadcrumbs.slice(0, -1),
        { title: t('Select location'), href: getStepUrl('location') },
        {
          title: t('NETWORK_AFFILIATION.CHOOSE_NETWORK_AFFILIATION'),
          href: getStepUrl('network-affiliation'),
        },
        {
          title: t('PRIMARY_CARE_PROVIDER.CONFIRM_NEW_PCP_BREADCRUMB'),
          href: '',
        },
      ]
    : breadcrumbs;

  if (currentStep === 'confirm') {
    return (
      <ConfirmPCPLocation
        breadcrumbs={confirmBreadcrumbs}
        curIsStaffModel={
          curPCP?.pcpInfo?.isStaffModel &&
          (enableAllStaffModelEni || enableAllStaffModelMnr)
        }
        curNetworkTypeCode={curPCP?.pcpInfo?.networkTypeCode}
        curPCPId={curPCP?.pcpInfo?.pcpID}
        curProviderId={curPCP?.pcpInfo?.providerID}
        curProviderLocationId={''}
        curProviderType={curPCP?.pcpInfo?.providerType}
        currentMember={currentMember}
        handleBackButtonClick={handleBackButtonClick}
        handleCancelConfirm={handleCancelConfirm}
        handleFinishAndReturn={handleFinishAndReturn}
        handleSaveLocation={handleSaveLocation}
        newProvider={providerDetails}
        newProviderLocation={selectedLocation}
        selectedNetworkAffiliation={selectedNetworkAffiliation}
        targetUrl={requestData?.targetUrl}
      />
    );
  }

  const locationHeadingContent = isNavigatingMaestro ? (
    <ChooseLocationHeading level={1} size="md">
      {t('CHOOSE_PCP_FLOW.CHOOSE_PROVIDER_LOCATION')}
    </ChooseLocationHeading>
  ) : (
    <ChoosePCPLocationHeading
      formattedProviderName={formattedProviderName}
      handleShowErrorModalSimulationReset={handleShowErrorModalSimulationReset}
      handleShowErrorModalSimulationSubmit={
        handleShowErrorModalSimulationSubmit
      }
      hasMultipleLocations={hasMultipleLocations}
      isMobileMapView={isMobileMapView}
    />
  );

  const locationsContent = isNavigatingMaestro ? (
    <LocationsListView
      isMobileMapView={isMobileMapView}
      providerLocations={providerLocations}
      saveAndCancelButtons={saveAndCancelButtons}
      savedLocationOption={savedLocationOption}
      setIsMobileMapView={setIsMobileMapView}
      setMobileRouteView={setMobileRouteView}
      setSavedLocationOption={setSavedLocationOption}
      useStickyButtonRow={useStickyButtonRow}
    />
  ) : (
    <LocationsMapViewWrap
      handleBackButtonClick={handleBackButtonClick}
      hasMultipleLocations={hasMultipleLocations}
      isMobileMapView={isMobileMapView}
      map={map}
      mobileRouteView={mobileRouteView}
      mobileScreen={mobileScreen}
      providerLocations={providerLocations}
      routeEndCoords={routeEndCoords}
      saveAndCancelButtons={saveAndCancelButtons}
      setIsMobileMapView={setIsMobileMapView}
      setMobileRouteView={setMobileRouteView}
      setRouteEndCoords={setRouteEndCoords}
      setSelectedAddressIndex={setSelectedAddressIndex}
      useStickyButtonRow={useStickyButtonRow}
    />
  );

  const stickyButtonRowContent =
    useStickyButtonRow && !isMobileMapView ? (
      <ButtonRowBox color="white" padding="0">
        {saveAndCancelButtons}
      </ButtonRowBox>
    ) : null;

  return (
    <div style={{ overflow: 'initial', position: 'relative' }}>
      <React.Fragment>
        {population === PopulationType.UNET ? (
          <UpdateErrorModal
            handleClose={handleCloseModal}
            isOpen={showErrorModal}
            primaryPhoneNumber={primaryPhoneNumber}
            searchButtonVariant={showButtonVariant}
          />
        ) : (
          <UpdatePCPErrorModal
            dependentSeqNbr={dependentSeqNbr}
            errorCode={errorCode}
            handleClose={handleCloseModal}
            isOpen={showErrorModal}
            populationType={errorModalPopulationType}
            primaryPhoneNumber={primaryPhoneNumber}
            resultsErrorMessage={errorMessage}
          />
        )}
        {!(mobileRouteView && mobileScreen) ? (
          <PCPHeaderContainer
            data-auto-testid="choose-pcp-location-container"
            data-testid="choose-pcp-location-container"
          >
            <ContentWrapperComponent>
              <PCPCardWrapper>
                <Breadcrumb
                  breadcrumbs={breadcrumbs}
                  onBackButtonClick={handleBackButtonClick}
                />
                <SelectedPCPCard height={selectedPCPCardHeight} padding="10px">
                  <Layout.Group
                    css={{
                      'abyss-layout-group': {
                        display: 'flex',
                        alignItems: 'flex-start',
                      },
                    }}
                  >
                    <ChoosePCPLocationAvatar>
                      <ImageComponent
                        imgRef={imgRef}
                        imgSrc={imgSrc}
                        providerType={providerType}
                        sizeToken="$choosePCPLocationIconSize"
                      />
                    </ChoosePCPLocationAvatar>
                    <Layout.Stack alignItems="left" space={0}>
                      <Text
                        css={{
                          color: '#323334',
                          '@media (max-width: 744px)': { color: '#FFFFFF' },
                        }}
                        fontSize="lg"
                        fontWeight="bold"
                      >
                        <StyledProviderName
                          cssProps={{ removeTruncatedDataCard }}
                        >
                          {fullProviderName}
                        </StyledProviderName>
                      </Text>
                      <Text
                        css={{
                          fontSize: '12.64px',
                          fontWeight: '500',
                          color: '#4B4D4F',
                          '@media (max-width: 744px)': { color: '#FFFFFF' },
                        }}
                        fontSize="xs"
                      >
                        {specialties?.[0]}
                      </Text>
                    </Layout.Stack>
                  </Layout.Group>
                </SelectedPCPCard>
              </PCPCardWrapper>
            </ContentWrapperComponent>
          </PCPHeaderContainer>
        ) : null}
        {showErrorView ? (
          <UpdatePCPErrorView />
        ) : (
          <React.Fragment>
            <LocationWrapperComponent>
              <Box color="white" height="unset" padding="0px">
                {locationHeadingContent}
                {locationsContent}
              </Box>
            </LocationWrapperComponent>
            {stickyButtonRowContent}
          </React.Fragment>
        )}
      </React.Fragment>
    </div>
  );
};
